import React, { useState } from 'react';
import { Phone, MapPin, Mail, Clock, Send } from 'lucide-react';
import { contact } from '../data/mock';

export default function Contact({ heading = true }) {
  const [form, setForm] = useState({ name: '', email: '', company: '', service: '', message: '' });
  const [sent, setSent] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    const submissions = JSON.parse(localStorage.getItem('salihi_inquiries') || '[]');
    submissions.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem('salihi_inquiries', JSON.stringify(submissions));
    setSent(true);
    setTimeout(() => {
      setSent(false);
      setForm({ name: '', email: '', company: '', service: '', message: '' });
    }, 3500);
  };

  return (
    <section id="contact" className="relative py-20 sm:py-24 lg:py-28 bg-gradient-to-b from-[#0a1b35] to-[#0d2143] overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-25" />
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full" style={{ background: 'radial-gradient(ellipse, rgba(232,184,99,0.08) 0%, transparent 70%)' }} />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10">
        {heading && (
          <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
            <p className="text-[#e8b863] tracking-[0.28em] text-[11px] font-semibold uppercase mb-5">Get in touch</p>
            <h2 className="font-serif text-white font-medium text-[32px] sm:text-[40px] lg:text-[52px] leading-[1.08] tracking-tight">
              Let’s discuss what your organization
              <span className="italic gold-gradient"> needs to do next.</span>
            </h2>
            <p className="mt-5 sm:mt-6 text-white/72 text-[15.5px] lg:text-[17px] leading-relaxed">
              Share a few details and a senior consultant will be in touch within one business day.
            </p>
          </div>
        )}

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-5 space-y-4">
            <Info icon={Phone} label="Call us" value={contact.phone} href={`tel:${contact.phone.replace(/\s/g, '')}`} />
            <Info icon={Mail} label="Email us" value={contact.email} href={`mailto:${contact.email}`} />
            <Info icon={MapPin} label="Visit us" value={contact.address} />
            <Info icon={Clock} label="Office hours" value={contact.hours} />
          </div>

          <form
            onSubmit={submit}
            className="lg:col-span-7 bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/12 rounded-3xl p-6 sm:p-8 lg:p-10"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full Name" required>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" placeholder="Your full name" />
              </Field>
              <Field label="Email Address" required>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" placeholder="you@company.com" />
              </Field>
              <Field label="Organization">
                <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="input" placeholder="Company / Institution" />
              </Field>
              <Field label="Service of Interest">
                <select value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="input">
                  <option value="">Select a service…</option>
                  <option>Accounting &amp; Financial Reporting</option>
                  <option>Tax Advisory &amp; Compliance</option>
                  <option>Audit &amp; Internal Controls</option>
                  <option>Business Registration &amp; Licensing</option>
                  <option>Legal &amp; Regulatory Advisory</option>
                  <option>Capacity Building &amp; Training</option>
                  <option>Software &amp; ERP Solutions</option>
                  <option>Investigation &amp; Due Diligence</option>
                  <option>Project Management</option>
                  <option>Capital Raising</option>
                  <option>Corporate Strategy</option>
                  <option>HR &amp; Organizational Development</option>
                  <option>Procurement &amp; Supply Chain</option>
                  <option>Monitoring &amp; Evaluation</option>
                  <option>Other</option>
                </select>
              </Field>
            </div>

            <div className="mt-5">
              <Field label="How can we help?" required>
                <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input resize-none" placeholder="Briefly describe your project or challenge…" />
              </Field>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
              <p className="text-white/65 text-[12px] max-w-sm">
                By submitting this form you agree to be contacted regarding your inquiry. We respect your privacy.
              </p>
              <button
                type="submit"
                disabled={sent}
                className="group inline-flex items-center gap-2.5 px-7 py-4 rounded-full bg-gradient-to-r from-[#d49d3f] to-[#b8842a] hover:from-[#e8b863] hover:to-[#d49d3f] text-[#0a1b35] font-semibold text-[14px] tracking-wide transition-all duration-300 shadow-xl shadow-[#d49d3f]/25 hover:-translate-y-0.5 disabled:opacity-70"
              >
                {sent ? <>Sent · We’ll be in touch</> : <>Send Inquiry <Send className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></>}
              </button>
            </div>

            <style>{`
              .input { width: 100%; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.14); border-radius: 14px; color: #fff; padding: 14px 16px; font-size: 14.5px; outline: none; transition: border-color 0.25s, background 0.25s; }
              .input::placeholder { color: rgba(255,255,255,0.5); }
              .input:focus { border-color: rgba(232,184,99,0.6); background: rgba(255,255,255,0.07); }
              select.input option { background: #0a1b35; color: #fff; }
            `}</style>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] text-white/72 tracking-[0.18em] uppercase font-semibold mb-2">
        {label} {required && <span className="text-[#e8b863]">*</span>}
      </span>
      {children}
    </label>
  );
}

function Info({ icon: Icon, label, value, href }) {
  const inner = (
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 rounded-xl bg-[#e8b863]/15 border border-[#e8b863]/35 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-[#e8b863]" />
      </div>
      <div>
        <p className="text-[10px] text-[#e8b863] tracking-[0.22em] uppercase font-semibold mb-1">{label}</p>
        <p className="text-white text-[15px] sm:text-[16.5px] font-semibold leading-relaxed break-words">{value}</p>
      </div>
    </div>
  );
  return (
    <div className="bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/12 rounded-2xl p-6 sm:p-7 hover:border-[#e8b863]/35 transition-colors">
      {href ? <a href={href} className="hover:opacity-90">{inner}</a> : inner}
    </div>
  );
}
